//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { ComponentPropsWithoutRef } from 'react';
import {
  Slider as AriaSlider,
  Label,
  SliderOutput,
  SliderOutputProps,
  SliderProps,
  SliderThumb,
  SliderThumbProps,
  SliderTrack,
  SliderTrackProps,
} from 'react-aria-components';
import styled, { css } from 'styled-components';

import { TrackingIdPrefix } from '@models/TrackingId';
import { withTracking } from '@utils/withTracking';

/**
 * A slider allows a user to select one or more values within a range.
 * The slider can be customized with different parts to display the current value, track, and thumbs.
 *
 * `Slider` is the root component that contains all the parts of a slider.
 * @param minValue has a default value of 0
 * @param maxValue has a default value of 100
 * @param step has a default value of 1
 *
 * `Slider.Label` displays the label of the slider.
 * `Slider.Output` displays the current value of a slider as text.
 * `Slider.Track` is a container for one or more slider thumbs.
 * `Slider.Thumb` represents an individual value that the user can adjust within a slider track.
 *
 * `Slider.FillTrack` can be added to show styling between thumbs in the slider track.
 *
 * See [React Aria Slider](https://react-spectrum.adobe.com/react-aria/Slider.html) for more information.
 *
 * @example
 * <Slider
 *    defaultValue={50}
 *    formatOptions={{
 *      style: 'currency',
 *      currency: 'USD',
 *    }}
 *    maxValue={100}
 *    onChange={(value) => setValue(value)}
 *    step={1}
 *  >
 *   <Slider.Label>
 *    <span>Price</span>
 *   <Slider.Label>
 *   <Slider.Track>
 *      {({state}) => (
 *        <>
 *          <Slider.FillTrack maxPosition={state.getThumbPercent(0)} minPosition={0}/>
 *          <Slider.Thumb index={0} thumbLabel='Value' trackingIdPrefix={trackingIdPrefix}/>
 *        </>
 *        )
 *      }
 *   </Slider.Track>
 *   <Slider.Output>
 *      {({ state }) => `Value: ${state.getThumbValueLabel(0)}`}
 *   </Slider.Output>
 * </Slider>
 *
 */
export const Slider = ({ ...props }: SliderProps) => {
  return <AriaSlider {...props} />;
};

const Output = ({ ...props }: SliderOutputProps) => {
  return <StyledSliderOutput {...props} />;
};

export const StyledSliderOutput = styled(SliderOutput)`
  padding: 0;
`;

const Track = ({ ...props }: SliderTrackProps) => {
  return <StyledTrack {...props} />;
};

const StyledTrack = styled(SliderTrack)(
  ({ theme }) => css`
    position: relative;
    height: ${theme.spacings[3]};
    width: 100%;
    padding: ${theme.spacings[2]} 0;

    /* track line */
    &:before {
      content: '';
      position: absolute;
      display: block;
      height: 3px;
      width: 100%;
      top: 50%;
      transform: translateY(-50%);

      background-color: ${theme.colors.gray2};
      border-radius: ${theme.borderRadii.pill};
    }
  `,
);

interface FillTrackProps extends ComponentPropsWithoutRef<'div'> {
  maxPosition: number;
  minPosition?: number;
}

const FillTrack = ({
  maxPosition,
  minPosition = 0,
  ...props
}: FillTrackProps) => {
  return (
    <StyledFillTrack
      maxPosition={maxPosition}
      minPosition={minPosition}
      {...props}
    />
  );
};

const StyledFillTrack = styled.div<{
  minPosition: number;
  maxPosition: number;
}>(
  ({ maxPosition, minPosition, theme }) => css`
    position: absolute;
    top: 50%;
    left: ${minPosition * 100}%;
    height: 6px;
    width: calc(${maxPosition - minPosition} * 100%);
    border-radius: ${theme.borderRadii.pill};
    background-color: ${theme.colors.aliases.new};
    transform: translate(0, -50%);
  `,
);

interface ThumbProps extends Omit<SliderThumbProps, 'aria-label' | 'index'> {
  ariaLabel: string;
  index: number;
  trackingIdPrefix: TrackingIdPrefix;
}

const Thumb = ({
  ariaLabel,
  index,
  trackingIdPrefix,
  ...props
}: ThumbProps) => {
  return (
    <StyledThumb
      key={`${index}-${ariaLabel}`}
      aria-label={ariaLabel}
      index={index}
      trackingId={`${trackingIdPrefix}thumb-${index}`}
      {...props}
    />
  );
};

const StyledThumb = withTracking(
  styled(SliderThumb)(
    ({ theme }) => css`
      position: absolute;
      top: 50%;
      height: ${theme.spacings[3]};
      width: ${theme.spacings[3]};
      background-color: ${theme.colors.white};
      border: 1px solid ${theme.colors.gray2};
      border-radius: 50%;

      &[data-dragging] {
        background-color: ${theme.colors.gray1};
      }

      &[data-focused] {
        outline: 2px solid ${theme.colors.aliases.focus};
      }

      &[data-focus-visible] {
        outline: 2px solid ${theme.colors.aliases.focus};
      }
    `,
  ),
);

Slider.FillTrack = FillTrack;
Slider.Label = Label;
Slider.Output = Output;
Slider.Thumb = Thumb;
Slider.Track = Track;
